import { cleanAPI } from './config';
import { GOOGLE_CALLBACK_URI, SPOTIFY_CALLBACK_URI } from '@/constants';
import type { GoogleTokens, ServiceTokens } from '@/types';
import { handleApiError } from '@/utils/apiErrorHandler';
import { trackEvent } from '@/utils/analytics';

export interface AuthUser {
  id: string | number;
  email: string;
  username: string;
  [key: string]: unknown;
}

export interface AuthCallbackResponse {
  jwt: string;
  refreshToken: string;
  user: AuthUser;
}

const TOKEN_PATTERN = /^[A-Za-z0-9_\-.~+/=]+$/;

function isValidJWT(token: string): boolean {
  if (!token || typeof token !== 'string') return false;
  const parts = token.split('.');
  return parts.length === 3 && parts.every((part) => part.length > 0);
}

function validateGoogleTokens(tokens: GoogleTokens): void {
  if (!tokens.access_token && !tokens.id_token) {
    throw new Error('Invalid tokens: must provide either access_token or id_token');
  }
  if (tokens.access_token) {
    if (!TOKEN_PATTERN.test(tokens.access_token)) {
      throw new Error('Invalid access_token: contains invalid characters');
    }
    if (tokens.access_token.length < 20 || tokens.access_token.length > 2500) {
      throw new Error('Invalid access_token: unexpected length');
    }
  }
  if (tokens.id_token && !isValidJWT(tokens.id_token)) {
    throw new Error('Invalid id_token: not a valid JWT format');
  }
}

function validateSpotifyTokens(tokens: ServiceTokens): void {
  if (!tokens.access_token || !TOKEN_PATTERN.test(tokens.access_token)) {
    throw new Error('Invalid access_token');
  }
  if (tokens.access_token.length < 20 || tokens.access_token.length > 500) {
    throw new Error('Invalid access_token: unexpected length');
  }
}

function validateLanguage(language: string): void {
  if (!/^[a-z]{2}(-[A-Z]{2})?$/.test(language)) {
    throw new Error('Invalid language: must be in format "en" or "en-US"');
  }
}

/** Raw shape of the Strapi auth response — deliberately loose, normalized below. */
interface RawAuthResponse {
  data?: RawAuthResponse;
  jwt?: string;
  access_token?: string;
  token?: string;
  refreshToken?: string;
  refresh_token?: string;
  user?: AuthUser | string;
}

/**
 * Normalises the Strapi auth response (handles nested `data`, `jwt`/`access_token`
 * aliases, and stringified `user`).
 */
function normalizeAuthResponse(data: RawAuthResponse): AuthCallbackResponse {
  const responseData = data?.data || data;
  if (!responseData || typeof responseData !== 'object') {
    throw new Error('Invalid backend response: missing or malformed data');
  }

  const jwt = responseData.jwt || responseData.access_token || responseData.token;
  const refreshToken = responseData.refreshToken || responseData.refresh_token;

  if (!jwt || !isValidJWT(jwt)) {
    throw new Error('No valid JWT found in backend response.');
  }
  if (!refreshToken) {
    throw new Error('No refresh token found in backend response.');
  }

  let user = responseData.user;
  if (typeof user === 'string') {
    try {
      user = JSON.parse(user);
    } catch {
      user = {} as AuthUser;
    }
  }

  return { jwt, refreshToken, user: user as AuthUser };
}

export const googleCallback = async (
  tokens: GoogleTokens,
  language: string,
): Promise<AuthCallbackResponse> => {
  try {
    validateGoogleTokens(tokens);
    validateLanguage(language);

    const response = await cleanAPI.post(GOOGLE_CALLBACK_URI, {
      access_token: tokens.access_token,
      refresh_token: tokens.refresh_token,
      id_token: tokens.id_token,
      language,
      provider: 'google',
    });

    const auth = normalizeAuthResponse(response.data);
    trackEvent('Auth', 'sign-in', 'google');
    return auth;
  } catch (error) {
    return handleApiError(error, 'Google');
  }
};

export const spotifyCallback = async (
  tokens: ServiceTokens,
  language: string,
): Promise<AuthCallbackResponse> => {
  try {
    validateSpotifyTokens(tokens);
    validateLanguage(language);

    const response = await cleanAPI.post(SPOTIFY_CALLBACK_URI, {
      access_token: tokens.access_token,
      refresh_token: tokens.refresh_token || '',
      language,
      provider: 'spotify',
    });

    return normalizeAuthResponse(response.data);
  } catch (error) {
    return handleApiError(error, 'Spotify');
  }
};
